import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from '../../common/Modal'
import { RequestPostSettingCustomAlert, RoleMember, RoleTeam } from '../../../../states/types/setting'
import { IconName } from '@fortawesome/fontawesome-common-types'
import Tips from '../../common/Tips'
import { replaceAndStrip } from '../../../../utils/string'
import { compile } from '../../../../utils/search'
import { sortBy } from 'lodash'
import { nowDate } from '../../../../utils/date'
import LoadingSquare from '../../common/LoadingSquare'
import AddTeamAndMemberModule from '../AddTeamAndMemberModule'
import AddMemberGroupingModule from '../AddMemberGroupingModule'

interface Team {
  id: number
  scope: string
  name: string
  include_nested_teams: boolean | null
}

interface CustomAlertConditions {
  condition_type: string
  operator_type: string
  time?: string
  seconds?: number
  biz_point?: number
  rate?: number
  service_name?: string
}

interface SelectParam {
  members: {
    id: number
    name: string
  }[]
  teams: Team[]
}

interface Props {
  closeCallback: VoidFunction
  addCallback: (params: RequestPostSettingCustomAlert, callback: VoidFunction) => void
  teamList: RoleTeam[]
  memberList: RoleMember[]
  candidateServices: { id: number, name: string }[]
  isCandidateServicesLoading: boolean
  isDisplay: boolean
}

interface Errors {
  alert_title: string
  condition: string
  team: string
  category_time: string
  category_biz_point: string
  application_time: string
  application_biz_point: string
  slack: string
}

interface Condition {
  condition_type: string
  operator_type: string
  detail: string | null
  target: string | null
}

interface TemplateCondition {
  title: string[]
  value: string
  description: string
  bg_color: string
  icon: string
  conditions: Condition[]
}

interface DisplayApplicationList {
  name: string
  checked: boolean
  display: boolean
}

const SELECT_TYPE = [
  {
    title: ["働き方のアラート"],
    value: "work",
    description: "働き方に関するアラートを作成したい",
    bg_color: "#FF0000",
    icon: "building",
    conditions: [],
  },
  {
    title: ["アプリのアラート"],
    value: "application",
    description: "アプリの使用状況に関するアラートを作成したい",
    bg_color: "#008800",
    icon: "icons",
    conditions: [],
  },
  {
    title: ["仕分け率のアラート"],
    value: "rate",
    description: "業務仕分けに関するアラートを作成したい",
    bg_color: "#0000FF",
    icon: "tags",
    conditions: [],
  },
]

const SELECT_WORK = [
  {
    title: ["長時間勤務アラート"],
    value: "work_long_time",
    description: "長時間勤務しているユーザーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "stopwatch",
    conditions: [
      {
        condition_type: "daily_work_time",
        operator_type: "more_than_or_equal",
        detail: (12 * 3600).toString(),
        target: null,
      },
    ],
  },
  {
    title: ["残業アラート"],
    value: "work_over_time",
    description: "残業しているユーザーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "moon",
    conditions: [
      {
        condition_type: "daily_over_time",
        operator_type: "more_than_or_equal",
        detail: (2 * 3600).toString(),
        target: null,
      },
    ],
  },
  {
    title: ["早朝勤務アラート"],
    value: "work_early_time",
    description: "早朝勤務しているユーザーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "cloud-sun",
    conditions: [
      {
        condition_type: "start_time",
        operator_type: "less_than_or_equal",
        detail: "07:00",
        target: null,
      },
    ],
  },
  {
    title: ["深夜勤務アラート"],
    value: "work_later_time",
    description: "深夜勤務しているユーザーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "cloud-moon",
    conditions: [
      {
        condition_type: "end_time",
        operator_type: "more_than_or_equal",
        detail: "22:00",
        target: null,
      },
    ],
  },
  {
    title: ["休日勤務アラート"],
    value: "work_holiday",
    description: "休日勤務しているユーザーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "mug-hot",
    conditions: [
      {
        condition_type: "holiday_work_time",
        operator_type: "more_than_or_equal",
        detail: "3600",
        target: null,
      },
    ],
  },
]

const SELECT_APPLICATION = [
  {
    title: ["アプリ使用時間アラート"],
    value: "application_time",
    description: "アプリの使用時間を集計して、アラートします",
    bg_color: "#09b837",
    icon: "clock",
    conditions: [
      {
        condition_type: "application_time",
        operator_type: "more_than_or_equal",
        detail: "3600",
        target: null,
      },
    ],
  },
  {
    title: ["アプリ操作量アラート"],
    value: "application_biz",
    description: "アプリの操作量を集計して、アラートします",
    bg_color: "#09b837",
    icon: "keyboard",
    conditions: [
      {
        condition_type: "application_biz_point",
        operator_type: "more_than_or_equal",
        detail: "1000",
        target: null,
      },
    ],
  },
]

const SELECT_RATE = [
  {
    title: ["仕分け率が低い", "ユーザーのアラート"],
    value: "rate_low",
    description: "業務仕分け率が低いユーザーを発見して、アラートします",
    bg_color: "#2080fd",
    icon: "star-half",
    conditions: [
      {
        condition_type: "labeling_rate",
        operator_type: "less_than_or_equal",
        detail: "60",
        target: null,
      },
    ],
  },
  {
    title: ["仕分け率が100%でない", "ユーザーのアラート"],
    value: "rate_under_100",
    description: "業務仕分け率が100%でないユーザーを発見して、アラートします",
    bg_color: "#2080fd",
    icon: "star",
    conditions: [
      {
        condition_type: "labeling_rate",
        operator_type: "less_than_or_equal",
        detail: "99",
        target: null,
      },
    ],
  },
]

const SCOPE_LABEL: Record<string, string> = {
  both: "マネージャー・メンバー",
  manager: "マネージャー",
  member: "メンバー",
}

const CreateCustomAlertWizardModal: FC<Props> = (props) => {
  const {
    closeCallback,
    addCallback,
    teamList,
    memberList,
    candidateServices,
    isCandidateServicesLoading,
    isDisplay,
  } = props

  const [step, setStep] = useState<string>("intro")

  const initErrors = {
    alert_title: "",
    condition: "",
    team: "",
    category_time: "",
    category_biz_point: "",
    application_time: "",
    application_biz_point: "",
    slack: "",
  }

  const [alertTitle, setAlertTitle] = useState<string>("")

  const [selectCondition, setSelectCondition] = useState<TemplateCondition | null>(null)

  const [applicationList, setApplicationList] = useState<DisplayApplicationList[]>([])
  const [isDisplayApplicationSelectBox, setIsDisplayApplicationSelectBox] = useState<boolean>(false)
  const [searchApplicationWord, setSearchApplicationWord] = useState<string>("")

  const [selectSubjectTeams, setSelectSubjectTeams] = useState<Team[]>([])
  const [selectSubjectMemberIds, setSelectSubjectMemberIds] = useState<number[]>([])
  const [selectRecipientMemberIds, setSelectRecipientMemberIds] = useState<number[]>([])

  const [isAlertTargetTeam, setIsAlertTargetTeam] = useState<boolean>(true)

  const [errors, setErrors] = useState<Errors>(initErrors)

  const selectList = useMemo(() => {
    if (step === "select") {
      return SELECT_TYPE
    }
    if (step === "work") {
      return SELECT_WORK
    }
    if (step === "application") {
      return SELECT_APPLICATION
    }
    if (step === "rate") {
      return SELECT_RATE
    }
    return []
  }, [step])

  const getAllMemberList = (id: number | null): RoleMember[] => {
    const tmpTeams = teamList.filter((v) => v.parent_team_id === id)
    if (!tmpTeams.length) return []
    const tmpMembers = [...tmpTeams.map((v) => [...v.managers, ...v.members]).flat(), ...tmpTeams.map((v) => getAllMemberList(v.id).flat()).flat()]
    return Array.from(new Map(tmpMembers.map((v) => [v.id, v])).values())
  }

  const close = () => {
    setStep("intro")
    setApplicationList(applicationList?.map((value) => ({ ...value, checked: false, display: true })))
    closeCallback()
  }

  const changeSubject = (param: SelectParam) => {
    setSelectSubjectTeams(param.teams)
    setSelectSubjectMemberIds(param.members.map((v) => v.id))
  }

  const changeRecipient = (param: SelectParam) => {
    setSelectRecipientMemberIds(param.members.map((v) => v.id))
  }

  const selectTarget = (condition: TemplateCondition) => {
    setSelectCondition(condition)
    if (["application_time", "application_biz"].includes(condition.value)) {
      setStep("select_app")
    } else {
      setStep("subject")
    }
  }

  const replaceAndStripTitle = (event: React.FocusEvent<HTMLInputElement>) => {
    setAlertTitle(replaceAndStrip(event.target.value))
    if (event.target.value) {
      setErrors({
        ...errors,
        alert_title: "",
      })
    }
  }

  const clickApplication = (word: string) => {
    setIsDisplayApplicationSelectBox(false)
    const list = applicationList?.map((value) => (
      value.name === word
        ? { ...value, checked: true, display: true }
        : { ...value, checked: false, display: true }
    ))
    setApplicationList(list)
    setSearchApplicationWord("")
    setErrors({
      ...errors,
      application_biz_point: "",
    })
  }

  const searchApplication = (words: string) => {
    setSearchApplicationWord(words)
    if (!words) {
      setApplicationList(applicationList?.map((value) => ({ ...value, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = applicationList?.map((value) => {
      if (compile(word).test(value.name)) {
        return { ...value, display: true }
      }
      return { ...value, display: false }
    })
    setApplicationList(sortBy(list, 'id'))
  }

  const resetSearchApplication = () => {
    setSearchApplicationWord("")
    setApplicationList(applicationList?.map((value) => ({ ...value, display: true })))
  }

  const buildMemberChip = (member: RoleMember) => (
    <div
      style={{
        border: "solid 1px #CCCCCC",
        borderRadius: "20px",
        padding: "4px 8px",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        userSelect: "none",
        backgroundColor: "#FFFFFF",
      }}
      key={`chip_${member.id}`}
    >
      <img
        alt="profile"
        src={
          member.profile_image ?? `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`
        }
        className="profile_icon"
        style={{
          borderRadius: "50%",
          width: "14px",
        }}
      />
      {member.name}
    </div>
  )

  const buildSelect = () => {
    const tmp = []
    if (["select_app", "subject", "recipient", "name", "confirm"].includes(step)) {
      tmp.push(
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "0 8px",
            }}
          >
            アラート：
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                borderRadius: "4px",
                backgroundColor: selectCondition?.bg_color,
                color: "#FFFFFF",
                padding: "2px 4px",
              }}
            >
              <FontAwesomeIcon icon={['fas', selectCondition?.icon as IconName]} />
              {selectCondition?.title.join("")}
            </div>
          </div>
        </>,
      )
    }
    if (["subject", "recipient", "name", "confirm"].includes(step) && !!applicationList.find((value) => value.checked)) {
      tmp.push(
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "0 8px",
            }}
          >
            アラート対象アプリ：
            <div>
              {applicationList.find((value) => value.checked)?.name || "-"}
            </div>
          </div>
        </>,
      )
    }
    if (["recipient", "name", "confirm"].includes(step)) {
      tmp.push(
        <>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "4px",
              padding: "0 8px",
            }}
          >
            アラート対象範囲：
            <div
              style={{
                padding: "12px 8px 8px",
                backgroundColor: "#ffeded",
                border: "solid 1px #CCCCCC",
                borderRadius: "4px",
                display: "flex",
                flexFlow: "column",
                gap: "8px",
              }}
            >
              {
                !!selectSubjectTeams.length && (
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      flexFlow: "column",
                      gap: "4px",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "8px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        transform: "translateY(-50%)",
                        padding: "0 4px",
                        fontSize: "10px",
                        backgroundColor: "#ffeded",
                      }}
                    >
                      アラート対象チーム
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "4px",
                        maxHeight: "250px",
                        overflowY: "scroll",
                      }}
                    >
                      {
                        isAlertTargetTeam && selectSubjectTeams.map((v) => {
                          const tmpTeam = teamList.find((w) => w.id === v.id)
                          if (!tmpTeam) return (<></>)
                          return (
                            <div
                              style={{
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                              }}
                            >
                              <div
                                style={{
                                  backgroundColor: "#EEEEEE",
                                  padding: "4px 8px",
                                  borderRadius: "4px 4px 0 0",
                                  fontSize: "10px",
                                }}
                              >
                                {`${tmpTeam.name}の${SCOPE_LABEL[v.scope]}${v.include_nested_teams ? " + 配下チーム" : ""}`}
                              </div>
                              <div
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  padding: "12px 8px 8px",
                                  borderRadius: "0 0 4px 4px",
                                  display: "flex",
                                  flexFlow: "column",
                                  gap: "8px",
                                }}
                              >
                                {
                                  ["both", "manager"].includes(v.scope) && (
                                    <div
                                      style={{
                                        position: "relative",
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        padding: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: 0,
                                          transform: "translateY(-50%)",
                                          padding: "0 4px",
                                          fontSize: "10px",
                                          backgroundColor: "#FFFFFF",
                                        }}
                                      >
                                        マネージャー
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "4px",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {tmpTeam.managers.map((w) => buildMemberChip(w))}
                                      </div>
                                    </div>
                                  )
                                }
                                {
                                  ["both", "member"].includes(v.scope) && !v.include_nested_teams && (
                                    <div
                                      style={{
                                        position: "relative",
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        padding: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: 0,
                                          transform: "translateY(-50%)",
                                          padding: "0 4px",
                                          fontSize: "10px",
                                          backgroundColor: "#FFFFFF",
                                        }}
                                      >
                                        このチームのメンバー
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "4px",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {tmpTeam.members.map((w) => buildMemberChip(w))}
                                      </div>
                                    </div>
                                  )
                                }
                                {
                                  ["both", "member"].includes(v.scope) && v.include_nested_teams && (
                                    <div
                                      style={{
                                        position: "relative",
                                        border: "solid 1px #CCCCCC",
                                        borderRadius: "4px",
                                        padding: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: 0,
                                          transform: "translateY(-50%)",
                                          padding: "0 4px",
                                          fontSize: "10px",
                                          backgroundColor: "#FFFFFF",
                                        }}
                                      >
                                        このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "4px",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {
                                          Array.from(new Map([
                                            ...getAllMemberList(v.id || null),
                                            ...tmpTeam.members,
                                          ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((w) => (
                                            buildMemberChip(w)
                                          ))
                                        }
                                      </div>
                                    </div>
                                  )
                                }
                              </div>
                            </div>
                          )
                        })
                      }
                    </div>
                  </div>
                )
              }
              {
                !isAlertTargetTeam && !!selectSubjectMemberIds.length && (
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      flexFlow: "column",
                      gap: "4px",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "8px 4px 4px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        transform: "translateY(-50%)",
                        padding: "0 4px",
                        fontSize: "10px",
                        backgroundColor: "#ffeded",
                      }}
                    >
                      アラート対象ユーザー
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "4px",
                        maxHeight: "250px",
                        overflowY: "scroll",
                      }}
                    >
                      {
                        memberList.filter((v) => selectSubjectMemberIds.includes(v.id)).map((v) => buildMemberChip(v))
                      }
                    </div>
                  </div>
                )
              }
            </div>
          </div>
        </>,
      )
    }
    if (["name", "confirm"].includes(step)) {
      tmp.push(
        <>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "4px",
              padding: "0 8px",
            }}
          >
            アラート通知を受け取るユーザー：
            <div
              style={{
                padding: "12px 8px 8px",
                backgroundColor: "#e6fff3",
                border: "solid 1px #CCCCCC",
                borderRadius: "4px",
                display: "flex",
                flexFlow: "column",
                gap: "8px",
              }}
            >
              {
                !!selectRecipientMemberIds.length && (
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      flexFlow: "column",
                      gap: "4px",
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "8px 4px 4px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        transform: "translateY(-50%)",
                        padding: "0 4px",
                        fontSize: "10px",
                        backgroundColor: "#e6fff3",
                      }}
                    >
                      アラート通知を受け取るユーザー
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "4px",
                        maxHeight: "250px",
                        overflowY: "scroll",
                      }}
                    >
                      {
                        memberList.filter((v) => selectRecipientMemberIds.includes(v.id)).map((v) => buildMemberChip(v))
                      }
                    </div>
                  </div>
                )
              }
            </div>
          </div>
        </>,
      )
    }
    if (["confirm"].includes(step)) {
      tmp.push(
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "0 8px",
            }}
          >
            アラート名：
            <div>
              {alertTitle}
            </div>
          </div>
        </>,
      )
    }
    return (
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "8px",
          marginBottom: "8px",
          borderRadius: "4px",
          padding: "8px",
          backgroundColor: ["confirm"].includes(step) ? "#FFFFFF" : "#EEEEEE",
          border: ["confirm"].includes(step) ? "solid 1px #CCCCCC" : "none",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
          }}
        >
          {["confirm"].includes(step) ? "作成されるアラート" : "現在の選択"}
        </div>
        {tmp.map((v) => v)}
      </div>
    )
  }

  const generateRandomString = (length = 8) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const array = new Uint32Array(length)
    window.crypto.getRandomValues(array)
    let result = ''
    for (let i = 0;i < length;i += 1) {
      result += chars[array[i] % chars.length]
    }
    return result
  }

  const buildTmpName = () => {
    let tmpName = ""
    const suffix = generateRandomString()
    const prefix = nowDate("YYYYMMDD")
    if (applicationList.find((v) => v.checked)) {
      tmpName = `${prefix}_${selectCondition?.title.join("")}_${applicationList.find((v) => v.checked)?.name}_${suffix}`
    } else {
      tmpName = `${prefix}_${selectCondition?.title.join("")}_${suffix}`
    }
    setAlertTitle(tmpName)
  }

  const submit = () => {
    setStep("submit")
    const tmpConditions: CustomAlertConditions[] = []
    selectCondition?.conditions.forEach((v) => {
      if (["daily_work_time", "daily_over_time", "holiday_work_time"].includes(v.condition_type)) {
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          seconds: Number(v.detail),
        })
      } else if (["start_time", "end_time"].includes(v.condition_type)) {
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          time: v.detail || "10:00",
        })
      } else if (["application_time"].includes(v.condition_type)) {
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          seconds: Number(v.detail),
          service_name: applicationList.find((w) => w.checked)?.name || "",
        })
      } else if (["application_biz_point"].includes(v.condition_type)) {
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          biz_point: Number(v.detail),
          service_name: applicationList.find((w) => w.checked)?.name || "",
        })
      } else if (["labeling_rate"].includes(v.condition_type)) {
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          rate: Number(v.detail),
        })
      }
    })
    addCallback(
      {
        name: alertTitle,
        send_timing: "daily",
        send_time: "10:00",
        subject: {
          teams: isAlertTargetTeam ? selectSubjectTeams : [],
          member_ids: !isAlertTargetTeam ? selectSubjectMemberIds : [],
          notify_self: false,
        },
        recipient: {
          teams: [],
          member_ids: selectRecipientMemberIds,
          slack_channel_id: null,
        },
        conditions: tmpConditions,
      },
      () => {
        setStep("completed")
      },
    )
  }

  useEffect(() => {
    setApplicationList(
      candidateServices.map((value) => ({ name: value.name, checked: false, display: true })),
    )
  }, [isDisplay, candidateServices])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="create_custom_alert_wizard_modal custom_alert no_animation"
      closeCallback={close}
      closeable
    >
      <>
        {
          step === "intro" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  カスタムアラート簡単設定でカスタムアラートを作成しましょう！
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src="/static/images/custom_alert/image_1.png" alt="" />
                  </div>
                  <div
                    style={{
                      padding: "4px",
                      display: "flex",
                      flexFlow: "column",
                      gap: "4px",
                      color: "#333333",
                    }}
                  >
                    <div>
                      カスタムアラート簡単設定ではいくつかの質問に回答すると、カスタムアラートを作成できます！
                    </div>
                    <div>
                      作成したカスタムアラートは後からいつでも変更することができます。
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "24px",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  className="button"
                  onClick={() => setStep("select")}
                >
                  アラートを作成しましょう
                </button>
              </div>
            </>
          )
        }
        {
          ["select", "work", "application", "rate"].includes(step) && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  どんなカスタムアラートを作成しますか？
                </div>
                {
                  step !== "select" && (
                    <>
                      <div>
                        <button
                          type="button"
                          onClick={() => setStep("select")}
                          style={{
                            fontSize: "11px",
                          }}
                        >
                          &lt; ひとつ前へ戻る
                        </button>
                      </div>
                    </>
                  )
                }
                <div className="select_button_area">
                  {
                    selectList.map((v) => (
                      <React.Fragment key={v.title.join()}>
                        <Tips
                          description={v.description}
                          className=""
                          icon={false}
                          style={{
                            width: "calc((100% - 48px) / 3)",
                          }}
                        >
                          <button
                            type="button"
                            style={{
                              backgroundColor: v.bg_color,
                            }}
                            onClick={() => {
                              if (step === "select") {
                                setStep(v.value)
                              } else {
                                selectTarget(v)
                                setSelectSubjectMemberIds([])
                                setSelectSubjectTeams([])
                                setIsAlertTargetTeam(true)
                              }
                            }}
                          >
                            <div
                              style={{
                                fontSize: "40px",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', v.icon as IconName]} />
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                display: "flex",
                                flexFlow: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "4px",
                              }}
                            >
                              {v.title.map((w) => (
                                <div>
                                  {w}
                                </div>
                              ))}
                            </div>
                          </button>
                        </Tips>
                      </React.Fragment>
                    ))
                  }
                </div>
              </div>
            </>
          )
        }
        {
          step === "select_app" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  アラートの対象アプリを選びましょう
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      setApplicationList(applicationList?.map((value) => ({ ...value, checked: false })))
                      setStep("select")
                    }}
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    &lt; ひとつ前へ戻る
                  </button>
                </div>
                {buildSelect()}
                <div className="item">
                  <div className="title">
                    アラート対象アプリ
                  </div>
                  <div className="conditions_item select" style={{ width: "100%" }}>
                    <div
                      className="selected_item"
                      style={isCandidateServicesLoading ? { cursor: "not-allowed", opacity: 0.5 } : undefined}
                    >
                      <div
                        className="modal_trigger"
                        role="none"
                        onClick={() => {
                          if (isCandidateServicesLoading) return
                          setIsDisplayApplicationSelectBox(true)
                        }}
                      />
                      <p>
                        {
                          applicationList.find((value) => value.checked)?.name || "-"
                        }
                      </p>
                      <FontAwesomeIcon icon={['fas', 'caret-down']} className="icon" />
                    </div>
                    <div
                      className={`select_box custom_alert_select ${isDisplayApplicationSelectBox || "none"}`}
                      style={{ top: "unset" }}
                    >
                      <div className="search_area">
                        <FontAwesomeIcon
                          icon={['fas', 'search']}
                          className="icon"
                        />
                        <input
                          type="text"
                          placeholder="アプリ名で検索"
                          onChange={(event) => searchApplication(event.target.value)}
                          value={searchApplicationWord}
                        />
                        {
                          searchApplicationWord && (
                            <button type="button" className="reset" onClick={resetSearchApplication}>
                              <FontAwesomeIcon icon={['far', 'times-circle']} />
                            </button>
                          )
                        }
                      </div>
                      <div className="select_list">
                        <div
                          key={0}
                          className="select_row single"
                          onClick={() => clickApplication("-")}
                          role="none"
                        >
                          -
                        </div>
                        {
                          applicationList && applicationList.map((value: DisplayApplicationList) => (
                            value.display && (
                              <div
                                key={value.name}
                                className="select_row single"
                                onClick={() => clickApplication(value.name)}
                                role="none"
                              >
                                {value.name}
                              </div>
                            )
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      setStep("subject")
                      setIsDisplayApplicationSelectBox(false)
                      setSelectSubjectMemberIds([])
                      setSelectSubjectTeams([])
                    }}
                    disabled={!applicationList.find((value) => value.checked)}
                  >
                    次はアラート対象チーム・ユーザーを選びましょう
                  </button>
                </div>
              </div>
            </>
          )
        }
        {
          step === "subject" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  アラート対象のチームまたはユーザーを選びましょう
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("select")
                      setApplicationList(applicationList?.map((value) => ({ ...value, checked: false })))
                    }}
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    &lt; ひとつ前へ戻る
                  </button>
                </div>
                {buildSelect()}
                <div className="item">
                  <div className="title">
                    アラート対象範囲
                  </div>
                  <div className="conditions_item" style={{ width: "100%" }}>
                    <div>
                      <div
                        style={{
                          padding: "12px 8px 8px",
                          backgroundColor: "#ffeded",
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          display: "flex",
                          flexFlow: "column",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "16px",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            backgroundColor: "#FFFFFF",
                            padding: "4px",
                            fontSize: "10px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              alignItems: "center",
                              cursor: "pointer",
                              color: isAlertTargetTeam ? "#008800" : "#666666",
                            }}
                            onClick={() => setIsAlertTargetTeam(true)}
                            role="none"
                          >
                            <FontAwesomeIcon icon={['far', isAlertTargetTeam ? 'check-circle' : 'circle']} />
                            アラート対象チームを設定する
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              alignItems: "center",
                              cursor: "pointer",
                              color: !isAlertTargetTeam ? "#008800" : "#666666",
                            }}
                            onClick={() => setIsAlertTargetTeam(false)}
                            role="none"
                          >
                            <FontAwesomeIcon icon={['far', !isAlertTargetTeam ? 'check-circle' : 'circle']} />
                            アラート対象ユーザーを設定する
                          </div>
                        </div>
                        {
                          isAlertTargetTeam && !!selectSubjectTeams.length && (
                            <div
                              style={{
                                position: "relative",
                                display: "flex",
                                flexFlow: "column",
                                gap: "4px",
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                                padding: "8px",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  transform: "translateY(-50%)",
                                  padding: "0 4px",
                                  fontSize: "10px",
                                  backgroundColor: "#ffeded",
                                }}
                              >
                                アラート対象チーム
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  flexFlow: "column",
                                  gap: "4px",
                                  maxHeight: "250px",
                                  overflowY: "scroll",
                                }}
                              >
                                {
                                  selectSubjectTeams.map((v) => {
                                    const tmpTeam = teamList.find((w) => w.id === v.id)
                                    if (!tmpTeam) return (<></>)
                                    return (
                                      <div
                                        style={{
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            backgroundColor: "#EEEEEE",
                                            padding: "4px 8px",
                                            borderRadius: "4px 4px 0 0",
                                            fontSize: "10px",
                                          }}
                                        >
                                          {`${tmpTeam.name}の${SCOPE_LABEL[v.scope]}${v.include_nested_teams ? " + 配下チーム" : ""}`}
                                        </div>
                                        <div
                                          style={{
                                            backgroundColor: "#FFFFFF",
                                            padding: "12px 8px 8px",
                                            borderRadius: "0 0 4px 4px",
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "8px",
                                          }}
                                        >
                                          {
                                            ["both", "manager"].includes(v.scope) && (
                                              <div
                                                style={{
                                                  position: "relative",
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    transform: "translateY(-50%)",
                                                    padding: "0 4px",
                                                    fontSize: "10px",
                                                    backgroundColor: "#FFFFFF",
                                                  }}
                                                >
                                                  マネージャー
                                                </div>
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    gap: "4px",
                                                    flexWrap: "wrap",
                                                  }}
                                                >
                                                  {tmpTeam.managers.map((w) => buildMemberChip(w))}
                                                </div>
                                              </div>
                                            )
                                          }
                                          {
                                            ["both", "member"].includes(v.scope) && !v.include_nested_teams && (
                                              <div
                                                style={{
                                                  position: "relative",
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    transform: "translateY(-50%)",
                                                    padding: "0 4px",
                                                    fontSize: "10px",
                                                    backgroundColor: "#FFFFFF",
                                                  }}
                                                >
                                                  このチームのメンバー
                                                </div>
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    gap: "4px",
                                                    flexWrap: "wrap",
                                                  }}
                                                >
                                                  {tmpTeam.members.map((w) => buildMemberChip(w))}
                                                </div>
                                              </div>
                                            )
                                          }
                                          {
                                            ["both", "member"].includes(v.scope) && v.include_nested_teams && (
                                              <div
                                                style={{
                                                  position: "relative",
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "8px",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    transform: "translateY(-50%)",
                                                    padding: "0 4px",
                                                    fontSize: "10px",
                                                    backgroundColor: "#FFFFFF",
                                                  }}
                                                >
                                                  このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                                </div>
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    gap: "4px",
                                                    flexWrap: "wrap",
                                                  }}
                                                >
                                                  {
                                                    Array.from(new Map([
                                                      ...getAllMemberList(tmpTeam.id || null),
                                                      ...tmpTeam.members,
                                                    ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((w) => (
                                                      buildMemberChip(w)
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            )
                                          }
                                        </div>
                                      </div>
                                    )
                                  })
                                }
                              </div>
                            </div>
                          )
                        }
                        {
                          !isAlertTargetTeam && !!selectSubjectMemberIds.length && (
                            <div
                              style={{
                                position: "relative",
                                display: "flex",
                                flexFlow: "column",
                                gap: "4px",
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                                padding: "8px 4px 4px",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  transform: "translateY(-50%)",
                                  padding: "0 4px",
                                  fontSize: "10px",
                                  backgroundColor: "#ffeded",
                                }}
                              >
                                アラート対象ユーザー
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                  maxHeight: "250px",
                                  overflowY: "scroll",
                                }}
                              >
                                {
                                  memberList.filter((v) => selectSubjectMemberIds.includes(v.id)).map((v) => buildMemberChip(v))
                                }
                              </div>
                            </div>
                          )
                        }
                        {
                          isAlertTargetTeam ? (
                            <AddTeamAndMemberModule
                              selectMemberIdList={selectSubjectMemberIds}
                              selectTeamList={selectSubjectTeams}
                              callback={(result) => changeSubject(result)}
                              memberList={
                                memberList.map((v) => ({
                                  id: v.id, name: v.name, kana: v.kana, profile_image: v.profile_image, active: true,
                                }))
                              }
                              teamList={teamList}
                              defaultSelectScope="member"
                              displayType={isAlertTargetTeam ? "team" : "member"}
                            />
                          ) : (
                            <AddMemberGroupingModule
                              memberIdList={selectSubjectMemberIds}
                              callbackMembers={(val) => changeSubject({ members: val, teams: [] })}
                              memberList={
                                memberList.map((v) => ({ id: v.id, name: v.name, profile_image: v.profile_image, active: v.active }))
                              }
                              teamList={teamList.map((v) => ({
                                ...v,
                                team_id: v.id,
                                count: 0,
                                created_at: "",
                                managers: v.managers.map((w) => ({ ...w, contract: "", place: "" })),
                                members: v.members.map((w) => ({ ...w, contract: "", place: "" })),
                              }))}
                            />
                          )
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      setStep("recipient")
                      setSelectRecipientMemberIds([])
                    }}
                    disabled={(!isAlertTargetTeam && !selectSubjectMemberIds.length) || (isAlertTargetTeam && !selectSubjectTeams.length)}
                  >
                    次はアラート通知を受け取るユーザーを選びましょう
                  </button>
                </div>
              </div>
            </>
          )
        }
        {
          step === "recipient" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  アラート通知を受け取るユーザーを選びましょう
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("subject")
                    }}
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    &lt; ひとつ前へ戻る
                  </button>
                </div>
                {buildSelect()}
                <div className="item">
                  <div className="title">
                    アラート通知を受け取るユーザー
                  </div>
                  <div className="conditions_item" style={{ width: "100%" }}>
                    <div>
                      <div
                        style={{
                          padding: "12px 8px 8px",
                          backgroundColor: "#e6fff3",
                          border: "solid 1px #CCCCCC",
                          borderRadius: "4px",
                          display: "flex",
                          flexFlow: "column",
                          gap: "8px",
                        }}
                      >
                        {
                          !!selectRecipientMemberIds.length && (
                            <div
                              style={{
                                position: "relative",
                                display: "flex",
                                flexFlow: "column",
                                gap: "4px",
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                                padding: "8px 4px 4px",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  transform: "translateY(-50%)",
                                  padding: "0 4px",
                                  fontSize: "10px",
                                  backgroundColor: "#e6fff3",
                                }}
                              >
                                アラート通知を受け取るユーザー
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  flexWrap: "wrap",
                                  maxHeight: "250px",
                                  overflowY: "scroll",
                                }}
                              >
                                {
                                  memberList.filter((v) => selectRecipientMemberIds.includes(v.id)).map((v) => buildMemberChip(v))
                                }
                              </div>
                            </div>
                          )
                        }
                        <AddMemberGroupingModule
                          memberIdList={selectRecipientMemberIds}
                          callbackMembers={(val) => changeRecipient({ members: val, teams: [] })}
                          memberList={
                            memberList.map((v) => ({ id: v.id, name: v.name, profile_image: v.profile_image, active: v.active }))
                          }
                          teamList={teamList.map((v) => ({
                            ...v,
                            team_id: v.id,
                            count: 0,
                            created_at: "",
                            managers: v.managers.map((w) => ({ ...w, contract: "", place: "" })),
                            members: v.members.map((w) => ({ ...w, contract: "", place: "" })),
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      setStep("name")
                      buildTmpName()
                    }}
                    disabled={!selectRecipientMemberIds.length}
                  >
                    次は名前を決めましょう
                  </button>
                </div>
              </div>
            </>
          )
        }
        {
          step === "name" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  アラートの名前を決めましょう
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setStep("recipient")}
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    &lt; ひとつ前へ戻る
                  </button>
                </div>
                {buildSelect()}
                <div className="item">
                  <div className="title">
                    アラート名
                  </div>
                  <div className="detail row_1">
                    <input
                      type="text"
                      placeholder="わかりやすい名称にすると、通知の際に便利です"
                      onChange={(event) => setAlertTitle(event.target.value)}
                      onBlur={(event) => replaceAndStripTitle(event)}
                      value={alertTitle}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="button"
                    onClick={() => setStep("confirm")}
                    disabled={!alertTitle}
                  >
                    もう少しで作成完了です！
                  </button>
                </div>
              </div>
            </>
          )
        }
        {
          step === "confirm" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  作成されるアラートを確認しましょう
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setStep("name")}
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    &lt; ひとつ前へ戻る
                  </button>
                </div>
                {buildSelect()}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="button"
                    onClick={submit}
                    disabled={!alertTitle}
                  >
                    アラートを作成！
                  </button>
                </div>
              </div>
            </>
          )
        }
        {
          step === "submit" && (
            <>
              <LoadingSquare>
                <>
                  アラートを作成しています…
                </>
              </LoadingSquare>
            </>
          )
        }
        {
          step === "completed" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  カスタムアラートの作成が完了しました！
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src="/static/images/custom_alert/image_2.png" alt="" />
                  </div>
                  <div
                    style={{
                      padding: "4px",
                      display: "flex",
                      flexFlow: "column",
                      gap: "4px",
                      color: "#333333",
                    }}
                  >
                    <div>
                      カスタムアラートの作成が完了しました！
                    </div>
                    <div>
                      細かい条件の変更・追加は「アラートの編集」からおこなうことができます！
                    </div>
                    <div>
                      様々な条件設定を駆使して、アラートをご活用ください！
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "24px",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  className="button"
                  onClick={close}
                >
                  アラート作成完了！
                </button>
              </div>
            </>
          )
        }
      </>
    </Modal>
  )
}

export default CreateCustomAlertWizardModal
